import { useReactiveVar } from '@apollo/client';
import { faEye, faFilter } from '@fortawesome/free-solid-svg-icons';
import { debounce, isEqual } from 'lodash';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import FilterGroup from './FilterGroup';
import ResetFiltersButton from './ResetFiltersButton';
import WeekNav from './WeekNav';
import ClientPill from './pills/ClientPill';
import FillRatePill from './pills/FillRatePill';
import RegionPill from './pills/RegionPill';
import SkillsPill from './pills/SkillsPill';
import StatusPill from './pills/StatusPill';
import TypesPill from './pills/TypesPill';
import './index.css';

import { useGetUserFilterQuery, useUserFilterSaveMutation } from '@/graphql';
import { useAppDispatch, useAppSelector } from '@/hooks/store';
import useAuth from '@/hooks/useAuth';
import {
  resetDashboardFilters,
  selectDashboardFilters,
  setDashboardAccounts,
  setDashboardCustomerIds,
  setDashboardFillStatus,
  setDashboardFilters,
  setDashboardJobStatus,
  setDashboardRegionIds,
  setDashboardSkillIds,
  setDashboardVisibility,
  setJobDashboardStatus,
} from '@/store/slices/filtersSlice';
import {
  CurrentCustomerAdminFragment,
  FillStatusEnum,
  JobDashboardStatusEnum,
  JobStatusEnum,
  UserFilterTypeEnum,
  VisibilityStatusEnum,
} from '@/types/graphql';
import { currentAdminVar } from '@/util/apollo/cache';
import { expandKeys, shortenKeys, updateUrlParams } from '@/util/dashboard';

type Props = {
  startDate: Date;
  onPrevWeek: () => void;
  onNextWeek: () => void;
};

/**
 * Owns the dashboard week navigation, all six filter pills, and persistence
 * to Redux + URL + the `userFilter` mutation. The shift grid reads its
 * filters from Redux via `selectDashboardFilters`.
 */
const DashboardHeader = ({ startDate, onPrevWeek, onNextWeek }: Props) => {
  const dispatch = useAppDispatch();
  const filters = useAppSelector(selectDashboardFilters) || {};
  const { currentAdmin, isCurrentCustomerAdmin } = useAuth();
  const currentAdminUser = useReactiveVar(
    currentAdminVar,
  ) as CurrentCustomerAdminFragment;
  const customerAdmin = isCurrentCustomerAdmin(currentAdmin);
  const customerIdLock = customerAdmin
    ? currentAdminUser?.customer?.id
    : undefined;

  const { data: userFilterData } = useGetUserFilterQuery({
    variables: { filterType: UserFilterTypeEnum.DASHBOARD },
  });
  const [saveUserFilter] = useUserFilterSaveMutation();

  const hydratedRef = useRef(false);

  /** Hydrate Redux from URL (preferred) or saved userFilter on first mount. */
  useEffect(() => {
    if (hydratedRef.current) return;
    if (userFilterData === undefined) return;

    const urlFilters = new URLSearchParams(window.location.search).get('fts');
    let next: typeof filters | null = null;

    if (urlFilters) {
      try {
        next = expandKeys(JSON.parse(decodeURIComponent(urlFilters)));
      } catch {
        next = null;
      }
    }
    if (!next && userFilterData?.userFilter?.filterValue) {
      try {
        next = JSON.parse(
          decodeURIComponent(userFilterData.userFilter.filterValue),
        );
      } catch {
        next = null;
      }
    }

    if (next) {
      // Strip per-customer-admin keys that should be controlled by the role
      // gate, not by a stale saved filter.
      if (customerAdmin) {
        const stripped: typeof filters = { ...next };
        delete (stripped as { customerIds?: unknown }).customerIds;
        delete (stripped as { accounts?: unknown }).accounts;
        dispatch(setDashboardFilters(stripped));
      } else {
        dispatch(setDashboardFilters(next));
      }
    }
    hydratedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userFilterData]);

  /** Force customer admins onto their own customer regardless of saved state. */
  useEffect(() => {
    if (!customerAdmin || !customerIdLock) return;
    if (!isEqual(filters.customerIds, [customerIdLock])) {
      dispatch(setDashboardCustomerIds([customerIdLock]));
    }
  }, [customerAdmin, customerIdLock, filters.customerIds, dispatch]);

  /** Persist filter changes to URL + userFilter (debounced). */
  const lastSavedRef = useRef<string>('');
  const debouncedSave = useMemo(
    () =>
      debounce((serialized: string) => {
        if (serialized === lastSavedRef.current) return;
        lastSavedRef.current = serialized;
        saveUserFilter({
          variables: {
            filterType: UserFilterTypeEnum.DASHBOARD,
            filterValue: serialized,
          },
        }).catch(() => {
          /* swallow — local Redux + URL are authoritative for the session */
        });
      }, 600),
    [saveUserFilter],
  );

  useEffect(() => {
    if (!hydratedRef.current) return;
    // Strip per-role keys before persistence so customer admins don't poison
    // their saved filter with the auto-locked customerIds.
    const serializable = { ...filters };
    if (customerAdmin) {
      delete (serializable as { customerIds?: unknown }).customerIds;
      delete (serializable as { accounts?: unknown }).accounts;
    }
    const encoded = encodeURIComponent(
      JSON.stringify(shortenKeys(serializable)),
    );
    updateUrlParams({ fts: encoded });
    debouncedSave(encodeURIComponent(JSON.stringify(serializable)));
  }, [filters, customerAdmin, debouncedSave]);

  // ── Pill callbacks ───────────────────────────────────────────────────
  const onClientApply = useCallback(
    ({
      customerIds,
      accountIds,
    }: {
      customerIds: string[] | undefined;
      accountIds: string[] | undefined;
    }) => {
      dispatch(setDashboardCustomerIds(customerIds));
      dispatch(
        setDashboardAccounts(accountIds ? { value: accountIds } : undefined),
      );
    },
    [dispatch],
  );

  const onClientClear = useCallback(() => {
    dispatch(setDashboardCustomerIds(undefined));
    dispatch(setDashboardAccounts(undefined));
  }, [dispatch]);

  const onSkillsApply = useCallback(
    (next: string[] | undefined) => dispatch(setDashboardSkillIds(next)),
    [dispatch],
  );
  const onSkillsClear = useCallback(
    () => dispatch(setDashboardSkillIds(undefined)),
    [dispatch],
  );

  const onRegionApply = useCallback(
    (next: string[] | undefined) => dispatch(setDashboardRegionIds(next)),
    [dispatch],
  );
  const onRegionClear = useCallback(
    () => dispatch(setDashboardRegionIds(undefined)),
    [dispatch],
  );

  const onStatusApply = useCallback(
    (next: JobStatusEnum[] | undefined) =>
      dispatch(setDashboardJobStatus(next)),
    [dispatch],
  );
  const onStatusClear = useCallback(
    () => dispatch(setDashboardJobStatus(undefined)),
    [dispatch],
  );

  const onFillRateApply = useCallback(
    (next: FillStatusEnum) =>
      dispatch(
        setDashboardFillStatus(next === FillStatusEnum.ALL ? undefined : next),
      ),
    [dispatch],
  );
  const onFillRateClear = useCallback(
    () => dispatch(setDashboardFillStatus(undefined)),
    [dispatch],
  );

  const onTypesApply = useCallback(
    ({
      visibility,
      includesNoShow,
    }: {
      visibility: VisibilityStatusEnum[] | undefined;
      includesNoShow: boolean;
    }) => {
      dispatch(setDashboardVisibility(visibility));
      // Merge with whatever else jobDashboardStatus held — currently TypesPill
      // is the only consumer of NO_SHOW so we can fully overwrite.
      dispatch(
        setJobDashboardStatus(
          includesNoShow ? [JobDashboardStatusEnum.NO_SHOW] : undefined,
        ),
      );
    },
    [dispatch],
  );
  const onTypesClear = useCallback(() => {
    dispatch(setDashboardVisibility(undefined));
    dispatch(setJobDashboardStatus(undefined));
  }, [dispatch]);

  // ── Reset ────────────────────────────────────────────────────────────
  const hasActiveFilters =
    (filters.customerIds || []).length > 0 ||
    (filters.skillIds || []).length > 0 ||
    (filters.regionIds || []).length > 0 ||
    (filters.dashboardJobStatus || []).length > 0 ||
    (filters.dashboardVisibility || []).length > 0 ||
    (filters.jobDashboardStatus || []).length > 0 ||
    (filters.fillStatus !== undefined &&
      filters.fillStatus !== FillStatusEnum.ALL) ||
    Boolean(filters.accounts?.value?.length);

  const handleReset = useCallback(() => {
    dispatch(resetDashboardFilters());
    if (customerAdmin && customerIdLock) {
      dispatch(setDashboardCustomerIds([customerIdLock]));
    }
  }, [dispatch, customerAdmin, customerIdLock]);

  // Customer admins don't see the Client pill (it's locked to their org).
  const customerIdsForPill = filters.customerIds as string[] | undefined;
  const accountIdsForPill = filters.accounts?.value as string[] | undefined;

  return (
    <div className="dashboard-header">
      <WeekNav startDate={startDate} onPrev={onPrevWeek} onNext={onNextWeek} />
      <div className="filter-bar">
        <FilterGroup icon={faFilter} iconA11yLabel="Filter by attributes">
          {!customerAdmin && (
            <ClientPill
              customerIds={customerIdsForPill}
              accountIds={accountIdsForPill}
              onApply={onClientApply}
              onClear={onClientClear}
            />
          )}
          <SkillsPill
            value={filters.skillIds as string[] | undefined}
            onApply={onSkillsApply}
            onClear={onSkillsClear}
          />
          <RegionPill
            value={filters.regionIds as string[] | undefined}
            onApply={onRegionApply}
            onClear={onRegionClear}
          />
        </FilterGroup>

        <FilterGroup icon={faEye} iconA11yLabel="Filter by visibility">
          <StatusPill
            value={filters.dashboardJobStatus as JobStatusEnum[] | undefined}
            onApply={onStatusApply}
            onClear={onStatusClear}
          />
          <FillRatePill
            value={filters.fillStatus as FillStatusEnum | undefined}
            onApply={onFillRateApply}
            onClear={onFillRateClear}
          />
          <TypesPill
            visibility={
              filters.dashboardVisibility as VisibilityStatusEnum[] | undefined
            }
            jobDashboardStatus={
              filters.jobDashboardStatus as JobDashboardStatusEnum[] | undefined
            }
            onApply={onTypesApply}
            onClear={onTypesClear}
          />
        </FilterGroup>

        <ResetFiltersButton active={hasActiveFilters} onClick={handleReset} />
      </div>
    </div>
  );
};

export default DashboardHeader;
